import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Tabs } from "../../themes/Default/components/Tabs";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Button } from "../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { useTranslation } from "react-i18next";
import { noteTypesManagement } from "../../hooks/api/NoteTypesManagement/NoteTypesManagement";
import { employeeWeeklyReportManagement } from "../../hooks/api/EmployeeWeeklyReportManagement";
import { ButtonBack } from "../../themes/Default/components/TopNavigation/components/Button";
import DatePicker from "../../themes/Default/components/Form/components/DatePicker/DatePicker";
import SummerNote from "../../packages/SummerNote/SummerNote";
import { Slide, toast, ToastContainer } from "react-toastify";
import { Toaster } from "../../themes/Default/components/Toaster";
import { Title } from "../../themes/Default/components/Title";
import { Textarea } from "../../themes/Default/components/Form/components/Textarea";
import { roles } from "../../services/utils/Roles";
import { useUserToken } from "../../hooks/core/UserToken";

const EditWeeklyReport = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const { employeeWeeklyReport } = useParams();
    const [formErrors, setFormErrors] = useState([]);
    const { useUpdateEmployeeWeeklyReportMutation, useViewEmployeeWeeklyReportMutation, useSendEmployeeWeeklyReportMutation } = employeeWeeklyReportManagement;
    const [editReport] = useUpdateEmployeeWeeklyReportMutation();
    const [viewReport] = useViewEmployeeWeeklyReportMutation();
    const [sendReport] = useSendEmployeeWeeklyReportMutation();
    const { useNoteTypesListMutation } = noteTypesManagement;
    const [noteTypesList] = useNoteTypesListMutation();
    const [saving, setSaving] = useState(false);
    const [endDate, setEndDate] = useState([]);
    const [report, setReport] = useState(null);
    const [{ getDecoded }] = useUserToken();
    const decodedToken = getDecoded();

    const tabs = [{ title: t("details"), link: "/settings/reports", isActive: true }];

    const getEndDate = (startDate) => {
        if (!startDate) return "";
    
        const currentDate = new Date(startDate);
    
        let dayOfWeek = currentDate.getDay();
    
        const daysToAdd = dayOfWeek === 0 ? 7 : 7 - dayOfWeek;
    
        currentDate.setDate(currentDate.getDate() + daysToAdd);
    
        const day = String(currentDate.getDate()).padStart(2, "0");
        const month = String(currentDate.getMonth() + 1).padStart(2, "0");
        const year = currentDate.getFullYear();
    
        return `${day}.${month}.${year}`;
    };

    const onSend = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        sendReport({employeeWeeklyReport}).then((result) => {
                            if (result?.error) {
                                setFormErrors(result?.error?.data?.error?.violations);
                            } else {
                                navigate(`/reports`);
                            }
                            setSaving(false);
                        });
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const onSave = () => {
        if (formRef.current) {
            setSaving(true);
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        editReport({body: values, employeeWeeklyReport}).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
            } else {
                navigate(`/reports`);
            }
            setSaving(false);
        });
    };

    useEffect(() => {
        viewReport(employeeWeeklyReport).then((result) => {
            formRef.current.setValues(result?.data);
            setEndDate(result?.data?.endDate);
            setReport(result?.data);
        });
    }, [noteTypesList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={'/reports'} />
                <Button title={t("save")} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
                <Button title={t("send")} icon="fa-regular fa-paper-plane" onClick={() => onSend()}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{report?.employeeName + " (" + report?.startDateFormated + " - " + report?.endDate + ")"}</Title>
                <Breadcrumps>
                <Link title={t("report.title")} icon="fa-light fa-clipboard" />
                    <Link title={t("report.edit")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        startDate: "",
                        endDate: "",
                        progress: "",
                        teachingDifficulties: "",
                        additionalNote: "",
                        completedTasks: "",
                        resolvedChallenges: "",
                        weeklyPlanByDay: ""
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange, setFieldValue }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <DatePicker
                                        name="startDate"
                                        label={t("report.startDate")}
                                        required={true}
                                        value={values.startDate}
                                        onChange={async (e) => {
                                            const value = e.target.value;
                                            setFieldValue("startDate", value);
                                        
                                            const end = await getEndDate(value);
                                            setEndDate(end);
                                        }} 
                                        error={formErrors?.startDate}
                                        disabledWeekdays={[1,2,3,4,5,6]}
                                        disableFuture={true}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        label={t("report.endDate")}
                                        value={endDate} 
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        label={t("report.progress")}
                                        height="400px"
                                        name="progress"
                                        value={values.progress}
                                        onChange={handleChange}
                                        error={formErrors?.progress}
                                        saving={saving}
                                        hint={t("report.hintForProgress")}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        label={t("report.teachingDifficulties")}
                                        height="400px"
                                        name="teachingDifficulties"
                                        value={values.teachingDifficulties}
                                        onChange={handleChange}
                                        error={formErrors?.teachingDifficulties}
                                        saving={saving}
                                        hint={t("report.hintForTeachingDifficulties")}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        label={t("report.additionalNote")}
                                        height="400px"
                                        name="additionalNote"
                                        value={values.additionalNote}
                                        onChange={handleChange}
                                        error={formErrors?.additionalNote}
                                        saving={saving}
                                        hint={t("report.hintForAdditionalNote")}
                                    />
                                </Field>
                            </Row>
                            {(decodedToken?.roles.includes(roles.PROFESSOR_COORDINATOR)) && (
                                <>
                                    <Row>
                                        <Field isFullWidth={true}>
                                            <SummerNote
                                                label={t("Odrađeni nenastavni zadaci")}
                                                height="400px"
                                                name="completedTasks"
                                                value={values.completedTasks}
                                                onChange={handleChange}
                                                error={formErrors?.completedTasks}
                                                saving={saving}
                                            />
                                        </Field>
                                    </Row>
                                    <Row>
                                        <Field isFullWidth={true}>
                                            <SummerNote
                                                label={t("Rešeni izazovi-zapažanja")}
                                                height="400px"
                                                name="resolvedChallenges"
                                                value={values.resolvedChallenges}
                                                onChange={handleChange}
                                                error={formErrors?.resolvedChallenges}
                                                saving={saving}
                                            />
                                        </Field>
                                    </Row>
                                    <Row>
                                        <Field isFullWidth={true}>
                                            <SummerNote
                                                label={t("Plan za narednu nedelju po danima")}
                                                height="400px"
                                                name="weeklyPlanByDay"
                                                value={values.weeklyPlanByDay}
                                                onChange={handleChange}
                                                error={formErrors?.weeklyPlanByDay}
                                                saving={saving}
                                            />
                                        </Field>
                                    </Row>
                                </>
                            )}
                            <Row>
                                <Field isFullWidth={true}>
                                    <Textarea
                                        name="leaveRequestNote"
                                        label={t("Zatraži odmor")}
                                        onChange={handleChange}
                                        value={values.leaveRequestNote}
                                        rows={5}
                                        error={formErrors?.leaveRequestNote}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default EditWeeklyReport;
